import { useEffect, useMemo, useState } from "react";

import BottomMobileOptionsModal from "@/components/bottom-mobile-options-modal";
import SignupModal from "@/components/signup-modal";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import useReportContent, {
  ReportContentTypeEnum,
  ReportContentViolationEnum,
} from "@/graphql/mutations/use-report-content";
import useAuth from "@/hooks/use-auth";
import { useIsBreakPoint } from "@/hooks/use-is-break-point";
import type { UserType as CurrentUserType } from "@/store";

import useDeleteAccount from "../hooks/use-delete-account";
import useToggleUserBlock from "../hooks/use-toggle-user-block";
import type { UserType } from "../types";
import { DeleteIcon, MoreIcon, SyncIcon } from "./icons";
import ReportedModal, { useReportedModal } from "./reported-modal";

interface MenuOptionProps extends React.ComponentProps<typeof Button> {
  Icon?(props: React.ComponentProps<"svg">): React.ReactNode;
  isMenu: boolean;
}

function MenuOption({ children, Icon, isMenu, ...props }: MenuOptionProps) {
  return isMenu ? (
    <Button
      className="w-full justify-start rounded-[10px] bg-transparent p-2.5 text-14/[1.5em] text-white transition-colors hover:bg-white/10"
      {...props}
    >
      {children}
    </Button>
  ) : (
    <li>
      <Button
        className="flex items-center justify-start gap-2.5 p-2.5"
        {...props}
      >
        {!!Icon && <Icon className="block w-6 md:hidden" />}
        <span>{children}</span>
      </Button>
    </li>
  );
}

interface MyProfileOptionsProps {
  isMenu?: boolean;
  onClose?(): void;
}

function MyProfileOptions({
  isMenu = false,
  onClose = () => {
    /* No-op */
  },
}: MyProfileOptionsProps) {
  const deleteAccount = useDeleteAccount();

  const handleDeleteAccount = () => {
    if (deleteAccount()) {
      onClose();
    }
  };

  const handleSyncGoogle = () => {
    if (!window.Intercom) {
      return;
    }

    window.Intercom(
      "showNewMessage",
      "Please set up a Google Sheet synced to my Morty records."
    );
    onClose();
  };

  return (
    <>
      <MenuOption Icon={SyncIcon} isMenu={isMenu} onClick={handleSyncGoogle}>
        Google Sheets sync
      </MenuOption>
      {/* <MenuOption isMenu={isMenu}>Blocked users</MenuOption> */}
      {!isMenu && (
        <MenuOption
          Icon={DeleteIcon}
          isMenu={isMenu}
          onClick={handleDeleteAccount}
        >
          Delete account
        </MenuOption>
      )}
    </>
  );
}

interface UserProfileOptionsProps {
  isMenu?: boolean;
  isOpen: boolean;
  onBlock(): void;
  onClose?(): void;
  onReport(): void;
  user: UserType;
}

export function UserProfileOptions({
  isMenu = false,
  isOpen,
  onBlock,
  onClose = () => {
    /* No-op */
  },
  onReport,
  user,
}: UserProfileOptionsProps) {
  const [isReporting, setIsReporting] = useState(false);

  const { mutationContext } = useAuth();
  const [reportContent] = useReportContent();

  const handleBlock = () => {
    onBlock();
    handleClick();
  };

  const handleClick = () => onClose();

  const handleReport = (violation: ReportContentViolationEnum) => () => {
    reportContent(
      mutationContext({
        id: user.id,
        type: ReportContentTypeEnum.USER,
        violation,
      })
    );
    onReport();
    handleClick();
  };

  const handleReportOptions = () => setIsReporting(true);

  useEffect(() => {
    if (!isOpen) {
      setIsReporting(false);
    }
  }, [isOpen]);

  if (isReporting) {
    return (
      <>
        <MenuOption
          isMenu={isMenu}
          onClick={handleReport(ReportContentViolationEnum.SUSPICIOUS)}
        >
          Profile is suspicious or spam
        </MenuOption>
        <MenuOption
          isMenu={isMenu}
          onClick={handleReport(ReportContentViolationEnum.OFFENSIVE)}
        >
          User is offensive, abusive, or hateful
        </MenuOption>
      </>
    );
  }

  return (
    <>
      <MenuOption isMenu={isMenu} onClick={handleReportOptions}>
        Report user
      </MenuOption>
      <MenuOption isMenu={isMenu} onClick={handleBlock}>
        {user.isBlocked ? "Un-block" : "Block"} user
      </MenuOption>
    </>
  );
}

interface ModalButtonProps {
  currentUser: CurrentUserType | null;
  user: UserType;
}

export default function ModalButton({ currentUser, user }: ModalButtonProps) {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isSignupModalOpen, setIsSignupModalOpen] = useState(false);
  const isDesktop = useIsBreakPoint("md");

  // const buttonProps = useBreakpointValue({
  //   base: { onClick: optionModalProps.onOpen },
  //   lg: { as: MenuButton },
  // });

  const toggleUserBlock = useToggleUserBlock(user);
  const [isReportedModalOpen, setIsReportedModalOpen] = useReportedModal();

  const handleBlockUser = () => {
    if (!currentUser) {
      setIsSignupModalOpen(true);
      return;
    }

    toggleUserBlock();
  };

  const isMe = useMemo(
    () => !!(currentUser && currentUser.id === user.id),
    [currentUser, user]
  );

  return (
    <>
      <Popover onOpenChange={setIsMenuOpen} open={isMenuOpen}>
        <PopoverTrigger
          aria-label="More"
          className="flex size-10 shrink-0 items-center justify-center rounded-full border border-white/50 shadow-white transition-shadow hover:shadow-0-0-10"
        >
          <MoreIcon />
        </PopoverTrigger>
        {isDesktop ? (
          <PopoverContent
            align="start"
            className="flex w-auto min-w-56 flex-col gap-1.5 rounded-[15px] border-0 bg-space-gray-dark p-2.5 shadow-0-0-8 shadow-black/25"
          >
            {isMe ? (
              <MyProfileOptions isMenu onClose={() => setIsMenuOpen(false)} />
            ) : (
              <UserProfileOptions
                isMenu
                isOpen={isMenuOpen}
                onBlock={handleBlockUser}
                onClose={() => setIsMenuOpen(false)}
                onReport={() => setIsReportedModalOpen(true)}
                user={user}
              />
            )}
          </PopoverContent>
        ) : (
          <BottomMobileOptionsModal
            onOpenChange={setIsMenuOpen}
            open={isMenuOpen}
            title="Additional options"
          >
            <ul className="flex flex-col items-stretch gap-2.5">
              {isMe ? (
                <MyProfileOptions onClose={() => setIsMenuOpen(false)} />
              ) : (
                <UserProfileOptions
                  isOpen={isMenuOpen}
                  onBlock={handleBlockUser}
                  onClose={() => setIsMenuOpen(false)}
                  onReport={() => setIsReportedModalOpen(true)}
                  user={user}
                />
              )}
            </ul>
          </BottomMobileOptionsModal>
        )}
      </Popover>
      <SignupModal
        onOpenChange={setIsSignupModalOpen}
        open={isSignupModalOpen}
      />
      <ReportedModal
        onOpenChange={setIsReportedModalOpen}
        open={isReportedModalOpen}
      />
    </>
  );
}
